import React, { useEffect } from 'react';
import Experiments from '@wix/wix-experiments';
import { removeCheckoutSessionId } from '@wix/cashier-common/dist/src/utils/checkoutSessionId';
import { TopProps, WidgetCallbackProps } from '../../../types/WidgetProps';
import { PayPalOnApproveParams, PayPalOnShippingChangeParams } from '../../../types/paypal';
import {
  isValidShippingError,
  ShippingContact,
  ShippingContactRestricted,
  ShippingContactSelectedUpdate,
  ShippingErrors,
  UNEXPECTED_ERROR,
} from '../../../types/Shipping';
import { UpdateOrderParams } from '../../../actions/payment/updatePayPalOrder';
import { GetContactPaymentDetailsParams } from '../../../actions/payment/getPayPalContactPaymentDetails';
import { createPayPalOrder } from '../../../actions/payment/createPayPalOrder';
import { transformToShippingContact } from '../../../services/paypal/paypalTransformData';
import { WidgetFedopsLogger } from '../../Hoc/Fedops/FedopsProvider';
import { InteractionName } from '../../../types/InteractionName';
import { logGeneralError } from '../../../utils/sentry';
import { amountToLong } from '../../../utils/amountToLong';
import { getEmptyFields } from '../../../utils/requiredUtils';
import { BillingContact } from '../../../types/BillingContact';
import { OnCancel, OnClick } from '../../../types/ExternalContract';
import { PaymentDetailsParamsPayPal } from '../../../utils/serverAPI/createPayment';
import { Experiment } from '../../../types/Experiment';
import { PaymentsButtonPlace, PayPalFundingSource } from '../../../types/PaymentsButtonViewConfig';
import { PaymentBreakdown } from '../../../types/PaymentBreakdown';
import { OrderItem } from '../../../types/OrderItem';
import { DispatchAsyncAction } from '../../../types/DispatchAsyncAction';
import { PayPalPaymentState } from '../../../types/StoreState';
import { PayPalButton } from './PayPalButton';
import { useBI } from './hooks/useBI';
import { OwnProps } from './types';

export type PayPalControllerProps = PayPalControllerInnerProps & WidgetCallbackProps;
export interface ContactPaymentDetailsResponse {
  shippingContact: ShippingContact;
  billingContact: BillingContact;
}

export type PayPalControllerInnerProps = OwnProps & {
  fedopsLogger: WidgetFedopsLogger;
  sessionId: string;
  experiments: Experiments;
  topProps: TopProps;
  widgetRootWrapperDh: string;
  paymentData: PayPalPaymentState;
  onClick: OnClick;
  createOrder: DispatchAsyncAction<typeof createPayPalOrder>;
  updateOrder(params: UpdateOrderParams): Promise<string>;
  createPaymentDetails(params: PaymentDetailsParamsPayPal): Promise<string>;
  getContactPaymentDetails(params: GetContactPaymentDetailsParams): Promise<ContactPaymentDetailsResponse>;
  onCancel: OnCancel;
  place: PaymentsButtonPlace;
};

export const PayPalController = (props: PayPalControllerProps) => {
  const { experiments } = props;
  const bi = useBI({ fundingSource: props.fundingSource, place: props.place });

  const onCancel = async (data: { orderID: string }) => {
    bi.reportModalClosed(data);
    props.onCancel();
  };

  const onClick = async (data: { fundingSource: PayPalFundingSource }, actions) => {
    bi.reportFastCheckoutButtonClicked(data);

    if (props.onClick) {
      let canceled: boolean;
      try {
        const result = await Promise.resolve(props.onClick());

        canceled = result.canceled;

        if (canceled) {
          bi.reportErrorDuringValidation(data);
        }
      } catch (e) {
        canceled = true;
        logGeneralError({
          message: 'props.onClick() throws exception',
          fingerprint: ['payPal', 'onClick'],
          payload: e,
        });
      }
      if (canceled) {
        return actions.reject();
      }
    }
    props.fedopsLogger.interactionStarted(InteractionName.PayPalCreateOrder);
    return actions.resolve();
  };

  const createOrder = async () => {
    const {
      currency,
      requestShipping,
      meta: { appDefId, appInstanceId },
    } = props.topProps;

    bi.reportInitCheckoutStart();

    let paymentAmount: string, paymentBreakdown: PaymentBreakdown, orderItems: OrderItem[];

    try {
      if (typeof props.topProps.getInitialPaymentData === 'function') {
        try {
          bi.reportGettingPaymentDataFromVerticalStart();
          const result = await props.topProps.getInitialPaymentData();
          paymentAmount = result.paymentAmount;
          paymentBreakdown = result.paymentBreakdown;
          orderItems = result.orderItems;

          bi.reportGettingPaymentDataFromVerticalSuccess({ paymentBreakdown, orderItems, paymentAmount });
        } catch (error) {
          bi.reportGettingPaymentDataFromVerticalFailure({ error });
          throw error;
        }
      } else {
        paymentAmount = props.topProps.paymentAmount;
        paymentBreakdown = props.topProps.paymentBreakdown;
        orderItems = props.topProps.orderItems;
      }

      const { orderId, cashierOrderId, chargeId } = await props.createOrder({
        paymentAmount,
        currency,
        requestShipping,
        paymentBreakdown,
        orderItems,
        appDefId,
        appInstanceId,
      });

      props.fedopsLogger.interactionEnded(InteractionName.PayPalCreateOrder);
      bi.reportInitCheckoutSuccess({ orderId, chargeId, cashierOrderId, paymentAmount });

      return orderId;
    } catch (error) {
      bi.reportInitCheckoutFailure({ error, paymentAmount });
    }
  };

  const getOnShippingChangeCallback = () => {
    /*
     * Venmo doesn't support shipping, so to properly display the button
     * we need to pass onShippingChange as undefined
     */
    if (props.fundingSource === window.paypal.FUNDING.VENMO) {
      return undefined;
    }
    // It's important to return undefined as callback for digital products
    // https://wix.atlassian.net/browse/EE-29930
    if (props.topProps.requestShipping) {
      return onShippingChange;
    }
  };

  const onShippingChange = async (data: PayPalOnShippingChangeParams, actions) => {
    let updatedPaymentInfo: ShippingContactSelectedUpdate;
    const { fedopsLogger } = props;
    const {
      currency,
      requestShipping,
      orderItems,
      meta: { appDefId, appInstanceId },
    } = props.topProps;

    bi.reportShippingMethodChanged(data);

    const shippingContact: ShippingContactRestricted = transformToShippingContact(data.shipping_address);

    try {
      updatedPaymentInfo = await props.onShippingContactSelected(shippingContact);
    } catch (e) {
      bi.reportShippingMethodNotAccepted({ params: data, errorReason: UNEXPECTED_ERROR });

      logGeneralError({
        message: 'PayPal onShippingChange cb throws error',
        payload: { e },
        fingerprint: ['onShippingChange throws'],
      });
      // rethrow. PayPal will handle errors while popup is open
      throw e;
    }

    const { error, paymentAmount, paymentBreakdown, shippingMethods } = updatedPaymentInfo;
    const isError = isValidShippingError(error);

    if (isError) {
      // When calling actions.reject(), PayPal shows error message
      // "${Merchant} doesn't ship to this location. Please use a different address"
      bi.reportShippingMethodNotAccepted({
        params: data,
        errorReason: ShippingErrors[error],
        amountLong: amountToLong(paymentAmount),
      });

      return actions.reject();
    }
    if (!error && paymentAmount) {
      fedopsLogger.interactionStarted(InteractionName.PayPalUpdateOrder);
      await props.updateOrder({
        paymentAmount,
        paymentBreakdown,
        shippingContact,
        shippingMethods,
        currency,
        requestShipping,
        orderItems,
        appDefId,
        appInstanceId,
      });
      fedopsLogger.interactionEnded(InteractionName.PayPalUpdateOrder);
      return actions.resolve();
    }
    logGeneralError({
      message: 'onShippingChange invoked with wrong error type or paymentAmount is not passed',
      payload: {
        error,
        updatedPaymentInfo,
      },
    });
    return actions.reject();
  };

  const onApprove = async (data: PayPalOnApproveParams, actions) => {
    const {
      fundingSource,
      sessionId,
      topProps: {
        meta: { appInstanceId, appDefId },
        shippingMethods: shippingMethodsFromTop,
      },
    } = props;

    bi.reportTrxAuthorized({ orderID: data.orderID });
    // For PayPal we should use shippingMethods from topProps.
    // Full shippingMethods functionality will be done in task
    // https://wix.atlassian.net/browse/PAY-20235
    //
    // This override allows to unblock Venmo
    // https://wix.atlassian.net/browse/PAY-20216
    const shippingMethods = fundingSource === window.paypal.FUNDING.VENMO ? null : shippingMethodsFromTop;

    if (typeof props.onPaymentAuthorizedInit === 'function') {
      try {
        props.onPaymentAuthorizedInit();
      } catch (e) {
        logGeneralError({
          message: 'onPaymentAuthorizedInit throws',
          payload: e,
        });
      }
    }

    props.fedopsLogger.interactionStarted(InteractionName.PayPalOnPaymentAuthorizedFetchData);
    let detailsId: string, contactData: ContactPaymentDetailsResponse;

    try {
      [detailsId, contactData] = await Promise.all([
        props.createPaymentDetails({
          orderId: data.orderID,
          payerId: data.payerID,
          sessionId,
        }),
        props.getContactPaymentDetails({
          orderId: data.orderID,
          appDefId,
          appInstanceId,
        }),
      ]);

      const emptyBillingFields = getEmptyFields(contactData.billingContact);
      const emptyShippingFields = getEmptyFields(contactData.shippingContact);

      bi.reportBuyerMissedShippingOrBillingAddressFields({
        orderID: data.orderID,
        emptyBillingFields: emptyBillingFields.join(','),
        emptyShippingFields: emptyShippingFields.join(','),
      });

      if (emptyBillingFields.includes('countryCode') || emptyBillingFields.includes('email')) {
        // Most likely order on PayPal is not APPROVED.
        // Send bi event to track 2 issues
        // 1. fast click https://wix.atlassian.net/browse/PAY-9433
        // 2. email is missing https://wix.atlassian.net/browse/PAY-11358
        bi.reportCountryMissed(data);
      }

      if (emptyBillingFields.includes('phone')) {
        contactData.billingContact.phone = undefined;
      }

      if (emptyShippingFields.includes('phone')) {
        contactData.shippingContact.phone = undefined;
      }

      props.fedopsLogger.interactionEnded(InteractionName.PayPalOnPaymentAuthorizedFetchData);
    } catch (error) {
      props.onPaymentAuthorized({ error });
      return;
    }

    try {
      const { result } = await props.onPaymentAuthorized({
        detailsId,
        shippingContact: contactData.shippingContact,
        billingContact: contactData.billingContact,
        shippingMethods,
      });

      bi.reportOnPaymentAuthorizedCallbackResult({
        result,
        orderID: data.orderID,
      });

      if (result === 'success') {
        removeCheckoutSessionId(props.topProps.meta.siteId);
      }

      if (result === 'error' && experiments.enabled(Experiment.RestartPayFlowForSpbIfErrorHappens)) {
        return actions.restart();
      }
    } catch (err) {
      logGeneralError({
        message: 'PayPal onPaymentAuthorized cb throws error',
        payload: {
          error: err,
        },
        fingerprint: ['onPaymentAuthorized'],
      });
    }
  };

  useEffect(() => {
    bi.reportButtonRendered();
  }, []);

  return (
    <PayPalButton
      fundingSource={props.fundingSource}
      widgetRootWrapperDh={props.widgetRootWrapperDh}
      onClick={onClick}
      onCancel={onCancel}
      createOrder={createOrder}
      onApprove={onApprove}
      onShippingChange={getOnShippingChangeCallback()}
    />
  );
};
